/**
 * AI Notes Socket.IO Hook
 * 
 * Manages WebSocket connection for real-time AI Notes features:
 * - Audio streaming
 * - Progress updates
 * - Contact extraction notifications
 * - Enrichment status
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { SocketEvents } from '@/types/ai-notes';
import { getAuthToken } from '@/lib/authToken';

interface UseAINotesSocketConfig {
  autoConnect?: boolean;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Error) => void;
}

export function useAINotesSocket(config: UseAINotesSocketConfig = {}) {
  const { autoConnect = false, onConnected, onDisconnected, onError } = config;
  
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const lastLoggedErrorRef = useRef<string>('');
  const errorCountRef = useRef<number>(0);
  const isMountedRef = useRef<boolean>(true);
  const maxConsecutiveErrorsRef = useRef<number>(0);
  const isConnectingRef = useRef<boolean>(false);
  
  // Get auth token (adjust based on your auth system)
  const getToken = useCallback(() => {
    return getAuthToken() || '';
  }, []);

  // Connect to Socket.IO server
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      return;
    }
    
    if (isConnectingRef.current) {
      return; // Already attempting to connect
    }

    const token = getToken();
    
    if (!token) {
      const error = new Error('No authentication token found. Please log in.');
      setError(error);
      onError?.(error);
      return;
    }

    // Reset tracking flags
    isMountedRef.current = true;
    isConnectingRef.current = true;
    maxConsecutiveErrorsRef.current = 0;
    errorCountRef.current = 0;
    lastLoggedErrorRef.current = '';

    // Determine server URL based on environment
    const serverUrl = import.meta.env.VITE_API_URL || 'http://localhost:3030';

    const socket = io(serverUrl, {
      auth: (cb: (data: { token: string }) => void) => {
        cb({ token: getToken() });
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 3, // Reduced from 5
      reconnectionDelay: 2000, // Start with 2 seconds
      reconnectionDelayMax: 10000, // Max 10 seconds between attempts
      timeout: 10000, // Connection timeout
    });

    // Connection handlers
    socket.on('connect', () => {
      if (!isMountedRef.current) return;
      
      setIsConnected(true);
      setError(null);
      isConnectingRef.current = false;
      lastLoggedErrorRef.current = '';
      errorCountRef.current = 0;
      maxConsecutiveErrorsRef.current = 0;
      onConnected?.();
    });

    socket.on('disconnect', (reason) => {
      if (!isMountedRef.current) return;
      
      setIsConnected(false);
      isConnectingRef.current = false;
      onDisconnected?.();
    });

    socket.on('connect_error', (err) => {
      if (!isMountedRef.current) return;
      
      maxConsecutiveErrorsRef.current++;
      isConnectingRef.current = false;
      const errorMsg = `Connection failed: ${err.message}`;
      
      // Stop reconnection after too many consecutive errors
      if (maxConsecutiveErrorsRef.current >= 3) {
        socket.io.opts.reconnection = false;
        socket.disconnect();
        return;
      }
      
      // Only log once and notify via callback
      if (lastLoggedErrorRef.current !== errorMsg) {
        lastLoggedErrorRef.current = errorMsg;
        errorCountRef.current = 1;
        const error = new Error(errorMsg);
        setError(error);
        onError?.(error);
      } else {
        errorCountRef.current++;
      }
    });

    socket.on('error', (err) => {
      if (!isMountedRef.current) return;
      
      const errorMsg = String(err);
      
      // Only notify once per unique error
      if (lastLoggedErrorRef.current !== errorMsg) {
        lastLoggedErrorRef.current = errorMsg;
        const error = new Error(err);
        setError(error);
        onError?.(error);
      }
    });

    socketRef.current = socket;
  }, [getToken, onConnected, onDisconnected, onError]);

  // Disconnect from server
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      // Disable reconnection before disconnecting
      socketRef.current.io.opts.reconnection = false;
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      isConnectingRef.current = false;
    }
  }, []);

  // Join conversation room
  const joinConversation = useCallback((conversationId: string) => {
    if (!socketRef.current?.connected) {
      console.warn('Socket not connected');
      return;
    }
    
    socketRef.current.emit('join-conversation', conversationId);
  }, []);

  // Leave conversation room
  const leaveConversation = useCallback((conversationId: string) => {
    if (!socketRef.current?.connected) return;
    
    socketRef.current.emit('leave-conversation', conversationId);
  }, []);

  // Start audio stream
  const startAudioStream = useCallback((conversationId: string, metadata: any = {}) => {
    if (!socketRef.current?.connected) {
      throw new Error('Socket not connected');
    }
    
    socketRef.current.emit('start-audio-stream', {
      conversationId,
      metadata,
    });
  }, []);

  // Stop audio stream
  const stopAudioStream = useCallback((conversationId: string) => {
    if (!socketRef.current?.connected) {
      throw new Error('Socket not connected');
    }
    
    socketRef.current.emit('stop-audio-stream', { conversationId });
  }, []);

  // Send audio chunk
  const sendAudioChunk = useCallback((conversationId: string, chunk: ArrayBuffer, timestamp: number) => {
    if (!socketRef.current?.connected) {
      console.warn('Socket not connected, dropping audio chunk');
      return;
    }
    
    socketRef.current.emit('audio-chunk', {
      conversationId,
      chunk,
      timestamp,
      isStreamComplete: false,
    });
  }, []);

  // Send final audio chunk (marks stream as complete)
  const sendFinalAudioChunk = useCallback((conversationId: string) => {
    if (!socketRef.current?.connected) {
      throw new Error('Socket not connected');
    }
    
    socketRef.current.emit('audio-chunk', {
      conversationId,
      chunk: new ArrayBuffer(0),
      timestamp: Date.now(),
      isStreamComplete: true,
    });
  }, []);

  // Subscribe to event
  const on = useCallback(<K extends keyof SocketEvents>(
    event: K,
    handler: SocketEvents[K]
  ) => {
    if (!socketRef.current) return;
    socketRef.current.on(event as string, handler as any);
  }, []);

  // Unsubscribe from event
  const off = useCallback(<K extends keyof SocketEvents>(
    event: K,
    handler?: SocketEvents[K]
  ) => {
    if (!socketRef.current) return;
    if (handler) {
      socketRef.current.off(event as string, handler as any);
    } else {
      socketRef.current.off(event as string);
    }
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Listen for token refresh events
    const handleTokenRefreshed = (event: CustomEvent) => {
      const { token } = event.detail;
      if (token && socketRef.current) {
        // Disconnect and reconnect with new token
        disconnect();
        setTimeout(() => {
          connect();
        }, 100);
      }
    };

    // Listen for token expiration
    const handleTokenExpired = () => {
      disconnect();
    };

    window.addEventListener('auth:token-refreshed', handleTokenRefreshed as EventListener);
    window.addEventListener('auth:token-expired', handleTokenExpired);

    return () => {
      isMountedRef.current = false;
      disconnect();
      window.removeEventListener('auth:token-refreshed', handleTokenRefreshed as EventListener);
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnect]); // Only reconnect if autoConnect changes

  return {
    socket: socketRef.current,
    isConnected,
    error,
    connect,
    disconnect,
    joinConversation,
    leaveConversation,
    startAudioStream,
    stopAudioStream,
    sendAudioChunk,
    sendFinalAudioChunk,
    on,
    off,
  };
}

// Export return type for context usage
export type UseAINotesSocketReturn = ReturnType<typeof useAINotesSocket>;
